/**
 * Maybourne Often Bought Together Component
 * Handles the interactive functionality for the often bought together section in cart drawer
 * Fetches recommendations via JavaScript to avoid theme conflicts
 * Integrated with the managers.js component architecture
 */

export default class OftenBoughtTogether {
  constructor(container = document) {
    this.container = container;
    this.elements = [];
    this.carousels = new Map();
    this.savedStates = new Map(); // Store carousel states during cart updates
    this.requestQueue = []; // Queue API requests to avoid rate limiting
    this.isProcessingQueue = false;
    this.isInitialized = false; // Prevent re-initialization
    
    this.init();
  }
  
  init() {
    if (this.isInitialized) {
      return;
    }
    
    console.log('OftenBoughtTogether: Initializing component');
    this.findElements();
    console.log('OftenBoughtTogether: Found elements:', this.elements.length);
    this.bindEvents();
    this.loadRecommendations();
    this.isInitialized = true;
  }
  
  findElements() {
    this.elements = this.container.querySelectorAll('.maybourne-often-bought-together-container');
  }
  
  bindEvents() {
    // Handle window resize for responsive behavior
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  async loadRecommendations() {
    // Queue all recommendation requests to avoid rate limiting
    const promises = Array.from(this.elements).map(element => this.queueRecommendationRequest(element));
    await Promise.all(promises);
  }
  
  async queueRecommendationRequest(element) {
    return new Promise((resolve) => {
      this.requestQueue.push({ element, resolve });
      this.processQueue();
    });
  }
  
  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) return;
    
    this.isProcessingQueue = true;
    
    while (this.requestQueue.length > 0) {
      const { element, resolve } = this.requestQueue.shift();
      
      try {
        await this.loadRecommendationsForElement(element);
        resolve();
      } catch (error) {
        resolve();
      }
      
      // Add a small delay between requests to respect rate limits
      if (this.requestQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    this.isProcessingQueue = false;
  }
  
  async loadRecommendationsForElement(element) {
    const productId = element.dataset.productId;
    const cartProducts = element.dataset.cartProducts ? element.dataset.cartProducts.split(',') : [];
    const title = element.dataset.title;
    const addText = element.dataset.addText;
    
    if (!productId) {
      return;
    }
    
    try {
      // Show loading state
      this.showLoading(element);
      
      // Fetch recommendations from Shopify
      const recommendations = await this.fetchRecommendations(productId);
      
      if (recommendations && recommendations.length > 0) {
        // Filter out products already in cart and get up to 4 available products
        const filteredProducts = recommendations
          .filter(product => !cartProducts.includes(product.id.toString()) && product.available)
          .slice(0, 4);
        
        if (filteredProducts.length > 0) {
          // Render the carousel
          this.renderCarousel(element, filteredProducts, title, addText);
          
          // Initialize carousel functionality
          this.initCarousel(element);
        } else {
          this.hideElement(element);
        }
      } else {
        this.hideElement(element);
      }
    } catch (error) {
      this.hideElement(element);
    }
  }
  
  async fetchRecommendations(productId) {
    try {
      // Try both locale-aware and locale-less endpoints
      const endpoints = [
        `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`,
        `/${document.documentElement.lang || 'en'}/recommendations/products.json?product_id=${productId}&limit=8&intent=related`
      ];
      
      for (const url of endpoints) {
        try {
          const response = await fetch(url);
          
          if (response.ok) {
            const data = await response.json();
            return data.products || [];
          } else if (response.status === 404) {
            continue;
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } catch (fetchError) {
          if (url === endpoints[endpoints.length - 1]) {
            throw fetchError;
          }
          continue;
        }
      }
      
      return [];
      
    } catch (error) {
      return [];
    }
  }
  
  showLoading(element) {
    const loading = element.querySelector('.maybourne-often-bought-together-loading');
    const content = element.querySelector('.maybourne-often-bought-together-content');
    
    if (loading) loading.style.display = 'block';
    if (content) content.style.display = 'none';
  }
  
  hideElement(element) {
    element.style.display = 'none';
  }
  
  renderCarousel(element, products, title, addText) {
    const content = element.querySelector('.maybourne-often-bought-together-content');
    const loading = element.querySelector('.maybourne-often-bought-together-loading');
    
    // Hide loading
    if (loading) loading.style.display = 'none';
    
    // Check if we have a saved state to restore
    const elementId = this.getElementId(element);
    const savedState = this.savedStates.get(elementId);
    const currentIndex = savedState ? savedState.currentIndex : 0;
    
    const carouselId = `often-bought-together-carousel-${elementId}`;
    
    const html = `
      <div class="maybourne-often-bought-together">
        <div class="maybourne-often-bought-together__header">
          <h3 class="maybourne-often-bought-together__title">${title}</h3>
        </div>

        <div class="maybourne-often-bought-together__carousel">
          <scroll-carousel id="${carouselId}" allow-drag class="maybourne-often-bought-together__products scroll-area snap-x" data-carousel>
            ${products.map((product, index) => `
              <div class="maybourne-often-bought-together__product${index === currentIndex ? ' is-active' : ''}" data-carousel-item="${index}">
                <div class="maybourne-often-bought-together__product-image">
                  ${product.featured_image ? `
                    <img src="${this.getImageUrl(product.featured_image, 240)}" 
                         alt="${product.title}" 
                         loading="lazy"
                         class="maybourne-often-bought-together__product-image-element"
                         sizes="(max-width: 699px) 70px, 120px">
                  ` : ''}
                </div>

                <div class="maybourne-often-bought-together__product-info">
                  <div class="maybourne-often-bought-together__product-content">
                    <h4 class="maybourne-often-bought-together__product-title">${product.title}</h4>
                    <p class="maybourne-often-bought-together__product-price">${this.formatMoney(product.price)}</p>
                  </div>

                  <div class="maybourne-often-bought-together__actions">
                    <button type="button" 
                            class="maybourne-often-bought-together__add-btn button button--outline button--subdued"
                            data-product-id="${product.id}"
                            data-variant-id="${product.variants[0].id}">
                      ${addText}
                    </button>
                  </div>
                </div>
              </div>
            `).join('')}
          </scroll-carousel>

          ${products.length > 1 ? `
            <carousel-navigation aria-controls="${carouselId}" class="maybourne-often-bought-together__dots page-dots">
              ${products.map((_, index) => `
                <button type="button" 
                        class="maybourne-often-bought-together__dot${index === currentIndex ? ' is-active' : ''}" 
                        data-carousel-dot="${index}"
                        aria-current="${index === currentIndex ? 'true' : 'false'}">
                  <span class="sr-only">Go to item ${index + 1}</span>
                </button>
              `).join('')}
            </carousel-navigation>
          ` : ''}
        </div>
      </div>
    `;
    
    content.innerHTML = html;
    content.style.display = 'block';
    
    // Clear saved state after restoration
    if (savedState) {
      this.savedStates.delete(elementId);
    }
  }
  
  getImageUrl(imageUrl, width) {
    // Convert Shopify image URL to specific width
    // Format: filename.jpg -> filename_WIDTHxWIDTH.jpg
    if (imageUrl.includes('.')) {
      const parts = imageUrl.split('.');
      const extension = parts.pop();
      const baseUrl = parts.join('.');
      return `${baseUrl}_${width}x${width}.${extension}`;
    }
    return imageUrl;
  }
  
  formatMoney(cents) {
    const money = (cents / 100).toFixed(2);
    
    // Use the active currency from Shopify
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      const currency = window.Shopify.currency.active;
      
      // Currency symbol mapping
      const currencySymbols = {
        'GBP': '£',
        'USD': '$',
        'EUR': '€',
        'JPY': '¥',
        'CAD': 'C$',
        'AUD': 'A$'
      };
      
      const symbol = currencySymbols[currency] || currency;
      
      // Format based on currency conventions
      if (currency === 'EUR') {
        return `${money}€`; // Euro typically goes after
      } else {
        return `${symbol}${money}`; // Most currencies go before
      }
    }
    
    // Fallback: Just the number
    return money;
  }
  
  initCarousel(element) {
    const carousel = element.querySelector('[data-carousel]');
    const products = element.querySelectorAll('[data-carousel-item]');
    const dots = element.querySelectorAll('[data-carousel-dot]');
    
    if (!carousel || products.length === 0) return;
    
    // Check if we have a saved state to restore
    const elementId = this.getElementId(element);
    const savedState = this.savedStates.get(elementId);
    const currentIndex = savedState ? savedState.currentIndex : 0;
    
    const carouselData = {
      element,
      carousel,
      products,
      dots,
      currentIndex
    };
    
    this.carousels.set(element, carouselData);
    
    // Bind events
    this.bindCarouselEvents(element);
    
    // Note: scroll-carousel component now handles all drag/swipe functionality natively
    
    // Update carousel state
    this.updateCarousel(carouselData);
  }
  
  bindCarouselEvents(element) {
    // Note: Dot navigation is now handled by the native carousel-navigation component
    
    // Handle add to cart buttons
    const buttons = element.querySelectorAll('.maybourne-often-bought-together__add-btn');
    console.log('OftenBoughtTogether: Found buttons:', buttons.length);
    buttons.forEach(button => {
      console.log('OftenBoughtTogether: Binding click event to button');
      button.addEventListener('click', (e) => {
        console.log('OftenBoughtTogether: Button click event triggered');
        this.handleAddToCart(e);
      });
    });
  }
  
  getElementId(element) {
    // Create a unique identifier for each carousel element
    const productId = element.dataset.productId;
    if (productId) return `carousel-${productId}`;
    
    // Fallback to position in DOM
    const allCarousels = this.container.querySelectorAll('.maybourne-often-bought-together-container');
    return `carousel-${Array.from(allCarousels).indexOf(element)}`;
  }
  
  saveCarouselStates() {
    // Save current states of all carousels before cart update
    this.carousels.forEach((carouselData, element) => {
      const elementId = this.getElementId(element);
      this.savedStates.set(elementId, {
        currentIndex: carouselData.currentIndex
      });
    });
  }
  
  // Note: setupTouchEvents removed - scroll-carousel component handles all drag/swipe natively
  
  goToSlide(element, index) {
    const carouselData = this.carousels.get(element);
    if (!carouselData || index < 0 || index >= carouselData.products.length) return;
    
    carouselData.currentIndex = index;
    this.updateCarousel(carouselData);
  }
  
  updateCarousel(carouselData) {
    // Note: The native scroll-carousel component now handles most state management
    // This method is kept for any custom logic that might be needed
    const { currentIndex } = carouselData;
    
    // Any custom state updates can be added here if needed
    console.log('OftenBoughtTogether: Carousel updated to index:', currentIndex);
  }
  
  handleResize() {
    // No special mobile handling needed - single item carousel for all screen sizes
  }
  
  async handleAddToCart(event) {
    event.preventDefault();
    
    const button = event.target;
    const variantId = button.dataset.variantId;
    
    // Create form data
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    
    // Debug: Log form data
    console.log('Form data entries:');
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }
    
    // Set loading state
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      });
      
      console.log('Add to cart response status:', response.status);
      
      if (response.ok) {
        // Product added successfully
        const data = await response.json();
        console.log('Product added successfully:', data);
        
        // Fetch updated cart to get current item count
        try {
          const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
          const cart = await cartResponse.json();
          
          // Dispatch cart change event for header cart count update
          document.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { cart: cart }
          }));
        } catch (cartError) {
          console.error('Failed to fetch updated cart:', cartError);
        }
        
        // Save carousel states before triggering cart update
        this.saveCarouselStates();
        
        // Show success feedback
        this.showSuccessFeedback(button);
        
        // Trigger cart update
        this.updateCartDrawer();
        
        // Dispatch custom event for other components to listen to
        window.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { product: data }
        }));
        
      } else {
        const errorText = await response.text();
        console.error('Add to cart failed:', response.status, errorText);
        throw new Error(`Failed to add product to cart: ${response.status} ${errorText}`);
      }
      
    } catch (error) {
      console.error('Add to cart error:', error);
      
      // Show error in alert so we can see it
      if (window.location.pathname.includes('/cart')) {
        alert(`Failed to add product to cart: ${error.message}`);
      }
      
      this.showErrorFeedback(button);
    } finally {
      // Remove loading state
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }
  
  updateCartDrawer() {
    // Check if we're in cart drawer or cart page context
    const cartDrawer = this.container.querySelector('cart-drawer');
    const isCartPage = window.location.pathname.includes('/cart');
    
    if (cartDrawer && cartDrawer.refresh) {
      // Cart drawer context - refresh the drawer without closing it
      cartDrawer.refresh();
    } else if (isCartPage) {
      // Cart page context - reload the page to show updated cart
      window.location.reload();
    } else {
      // Fallback: reload cart drawer content via fetch
      this.refreshCartDrawerContent();
    }
  }
  
  async refreshCartDrawerContent() {
    try {
      const response = await fetch(window.location.pathname + '?section_id=cart-drawer');
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCartDrawer = doc.querySelector('cart-drawer');
        
        if (newCartDrawer) {
          const currentCartDrawer = this.container.querySelector('cart-drawer');
          if (currentCartDrawer) {
            currentCartDrawer.replaceWith(newCartDrawer);
            // Re-initialize components for the new cart drawer
            this.refresh();
          }
        }
      }
    } catch (error) {
    }
  }
  
  showSuccessFeedback(button) {
    if (!button) return;
    
    const originalText = button.textContent;
    button.textContent = 'ADDED!';
    button.style.backgroundColor = '#059669';
    button.style.borderColor = '#059669';
    button.style.color = 'white';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.style.borderColor = '';
      button.style.color = '';
    }, 2000);
  }
  
  showErrorFeedback(button) {
    if (!button) return;
    
    const originalText = button.textContent;
    button.textContent = 'ERROR';
    button.style.backgroundColor = '#dc2626';
    button.style.borderColor = '#dc2626';
    button.style.color = 'white';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.style.borderColor = '';
      button.style.color = '';
    }, 2000);
  }
  
  refresh() {
    // Clear existing carousels
    this.carousels.clear();
    
    // Re-initialize
    this.findElements();
    this.bindEvents();
    this.loadRecommendations();
  }
  
  destroy() {
    // Clean up event listeners
    window.removeEventListener('resize', this.handleResize.bind(this));
    
    // Clear carousels and saved states
    this.carousels.clear();
    this.savedStates.clear();
    this.elements = [];
  }
} 